import React, { useState, useEffect, useRef } from 'react';
import API from '../../services/api'; 
import DocumentReportsPage from './DocumentReportsPage';

const DocPanel = () => {
    const [activeTab, setActiveTab] = useState('operations');
    const [stats, setStats] = useState({ total_bikes: 0, documents_received: 0, documents_pending: 0, documents_issued: 0 });
    
    // Single Receiving States
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedBike, setSelectedBike] = useState(null);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [singleForm, setSingleForm] = useState({ document_type: 'File', physical_location: '', received_by: '', remarks: '' });

    // Issuance Search States (Added for Issuance Section)
    const [issueQuery, setIssueQuery] = useState('');
    const [issueSearchResults, setIssueSearchResults] = useState([]);
    const [selectedIssueBike, setSelectedIssueBike] = useState(null);
    const [isIssueDropdownOpen, setIsIssueDropdownOpen] = useState(false);
    const [issueHighlightedIndex, setIssueHighlightedIndex] = useState(-1);

    const itemRefs = useRef([]);
    const issueItemRefs = useRef([]);

    // Bulk Receiving States
    const [lotInput, setLotInput] = useState('');
    const [lotBikes, setLotBikes] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [bulkForm, setBulkForm] = useState({ document_type: 'File', received_by: '', remarks: '' });

    // Issuance States
    const [issueForm, setIssueForm] = useState({ inventory_stock_id: '', issued_type: 'Physical Handover', dispatch_id: '', issued_to: '', remarks: '' });

    useEffect(() => {
        fetchStats();
    }, []);

    // ⚡ Real-time Auto Search for Receiving
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchQuery.trim().length >= 1) {
                performSearch(searchQuery, 'receive');
            } else {
                setSearchResults([]);
                setIsDropdownOpen(false);
                setHighlightedIndex(-1);
            }
        }, 250);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // ⚡ Real-time Auto Search for Issuance
    useEffect(() => {
        const timer = setTimeout(() => {
            if (issueQuery.trim().length >= 1) {
                performSearch(issueQuery, 'issue');
            } else {
                setIssueSearchResults([]);
                setIsIssueDropdownOpen(false);
                setIssueHighlightedIndex(-1);
            }
        }, 250);

        return () => clearTimeout(timer);
    }, [issueQuery]);

    const performSearch = async (query, type) => {
        try {
            const res = await API.get(`/documents/search-bike?query=${query}`);
            if (res.data.success) {
                if (type === 'receive') {
                    setSearchResults(res.data.data);
                    setIsDropdownOpen(true);
                    setHighlightedIndex(-1);
                } else {
                    setIssueSearchResults(res.data.data);
                    setIsIssueDropdownOpen(true);
                    setIssueHighlightedIndex(-1);
                }
            }
        } catch (err) { 
            console.error(err); 
        }
    };

    const handleKeyDown = (e) => {
        if (!isDropdownOpen || searchResults.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex((prevIndex) => {
                const nextIndex = prevIndex < searchResults.length - 1 ? prevIndex + 1 : 0;
                itemRefs.current[nextIndex]?.scrollIntoView({ block: 'nearest' });
                return nextIndex;
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex((prevIndex) => {
                const nextIndex = prevIndex > 0 ? prevIndex - 1 : searchResults.length - 1;
                itemRefs.current[nextIndex]?.scrollIntoView({ block: 'nearest' });
                return nextIndex;
            });
        } else if (e.key === 'Enter') {
            if (highlightedIndex >= 0 && highlightedIndex < searchResults.length) {
                e.preventDefault();
                selectBikeHandler(searchResults[highlightedIndex]);
            }
        } else if (e.key === 'Escape') {
            setIsDropdownOpen(false);
        }
    };

    const handleIssueKeyDown = (e) => {
        if (!isIssueDropdownOpen || issueSearchResults.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setIssueHighlightedIndex((prevIndex) => {
                const nextIndex = prevIndex < issueSearchResults.length - 1 ? prevIndex + 1 : 0;
                issueItemRefs.current[nextIndex]?.scrollIntoView({ block: 'nearest' });
                return nextIndex;
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setIssueHighlightedIndex((prevIndex) => {
                const nextIndex = prevIndex > 0 ? prevIndex - 1 : issueSearchResults.length - 1;
                issueItemRefs.current[nextIndex]?.scrollIntoView({ block: 'nearest' });
                return nextIndex;
            });
        } else if (e.key === 'Enter') {
            if (issueHighlightedIndex >= 0 && issueHighlightedIndex < issueSearchResults.length) {
                e.preventDefault();
                selectIssueBikeHandler(issueSearchResults[issueHighlightedIndex]);
            }
        } else if (e.key === 'Escape') {
            setIsIssueDropdownOpen(false);
        }
    };

    const selectBikeHandler = (bike) => {
        setSelectedBike(bike);
        setSearchQuery(`${bike.model_name} - Engine: ${bike.engine_number}`);
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
    };

    const selectIssueBikeHandler = (bike) => {
        setSelectedIssueBike(bike);
        setIssueQuery(`${bike.model_name} - Engine: ${bike.engine_number}`);
        setIssueForm({ ...issueForm, inventory_stock_id: bike.inventory_stock_id });
        setIsIssueDropdownOpen(false);
        setIssueHighlightedIndex(-1);
    };

    const fetchStats = async () => {
        try {
            const res = await API.get('/documents/dashboard-stats');
            if (res.data.success) setStats(res.data.data);
        } catch (err) {
            console.error("Error fetching stats:", err);
        }
    };

    const handleSingleReceiveSubmit = async (e) => {
        e.preventDefault();
        if (!selectedBike) return alert('Please select a bike first!');
        try {
            const res = await API.post('/documents/receive-single', {
                inventory_stock_id: selectedBike.inventory_stock_id,
                ...singleForm
            });
            alert(res.data.message);
            if (res.data.success) {
                setSelectedBike(null); setSearchQuery(''); setSearchResults([]);
                fetchStats();
            }
        } catch (err) { console.error(err); }
    };

    const handleLoadLot = async () => {
        if (!lotInput) return alert('Enter Lot or DO Number');
        try {
            const res = await API.get(`/documents/load-lot/${lotInput}`);
            if (res.data.success) {
                setLotBikes(res.data.data.map(b => ({ ...b, checked: true })));
                setSelectAll(true);
            }
        } catch (err) { console.error(err); }
    };

    const handleBulkReceiveSubmit = async (e) => {
        e.preventDefault();
        const selectedIds = lotBikes.filter(b => b.checked).map(b => b.inventory_stock_id);
        if (selectedIds.length === 0) return alert('No bikes selected!');
        try {
            const res = await API.post('/documents/receive-bulk', {
                inventory_stock_ids: selectedIds,
                ...bulkForm
            });
            alert(res.data.message);
            if (res.data.success) {
                setLotBikes([]); setLotInput('');
                fetchStats();
            }
        } catch (err) { console.error(err); }
    };

    const handleIssueSubmit = async (e) => {
        e.preventDefault();
        if (!issueForm.inventory_stock_id) return alert('Please select a bike from search to issue document!');
        if (issueForm.issued_type === 'Dispatch' && !issueForm.dispatch_id) {
            return alert('Dispatch ID is required when issuance type is Dispatch.');
        }

        try {
            const payload = { ...issueForm };
            if (payload.issued_type === 'Physical Handover') {
                payload.dispatch_id = '';
            }

            const res = await API.post('/documents/issue-document', payload);
            alert(res.data.message);
            if (res.data.success) {
                setIssueForm({ inventory_stock_id: '', issued_type: 'Physical Handover', dispatch_id: '', issued_to: '', remarks: '' });
                setIssueQuery('');
                setSelectedIssueBike(null);
                fetchStats();
            }
        } catch (err) { console.error(err); }
    };

    const completionPercentage = stats.total_bikes > 0 ? ((stats.documents_received / stats.total_bikes) * 100).toFixed(1) : 0;

    return (
        <div style={containerStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '2px solid #e2e8f0', paddingBottom: '10px' }}>
                <h2>📑 Doc Department</h2>
                <div>
                    <button onClick={() => setActiveTab('operations')} style={navTabBtn(activeTab === 'operations')}>Operations Panel</button>
                    <button onClick={() => setActiveTab('reports')} style={navTabBtn(activeTab === 'reports')}>📊 Detailed Reports</button>
                </div>
            </div>

            {activeTab === 'operations' ? (
                <div>
                    <div style={statsGridStyle}>
                        <div style={{ ...cardStyle, background: '#e0f2fe' }}><h5>Total Bikes</h5><h3>{stats.total_bikes}</h3></div>
                        <div style={{ ...cardStyle, background: '#dcfce7' }}><h5>Documents Received</h5><h3>{stats.documents_received}</h3></div>
                        <div style={{ ...cardStyle, background: '#fee2e2' }}><h5>Documents Pending</h5><h3>{stats.documents_pending}</h3></div>
                        <div style={{ ...cardStyle, background: '#fef3c7' }}><h5>Documents Issued</h5><h3>{stats.documents_issued}</h3></div>
                        <div style={{ ...cardStyle, background: '#f3e8ff', textAlign: 'center' }}>
                            <h5>Completion</h5><h3 style={{ color: '#7c3aed' }}>{completionPercentage}%</h3>
                        </div>
                    </div>

                    <div style={gridTwoCol}>
                        <div style={boxStyle}>
                            <h4>A. Individual Bike Document Receiving</h4>
                            <div style={{ position: 'relative', marginBottom: '15px' }}>
                                <input 
                                    type="text" 
                                    placeholder="Search by Engine No or Chassis No..." 
                                    value={searchQuery} 
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setSelectedBike(null);
                                    }} 
                                    onKeyDown={handleKeyDown}
                                    style={inputStyle} 
                                />
                                {isDropdownOpen && searchResults.length > 0 && (
                                    <div style={dropdownListStyle}>
                                        {searchResults.map((bike, index) => {
                                            const isHighlighted = highlightedIndex === index;
                                            return (
                                                <div 
                                                    key={bike.inventory_stock_id} 
                                                    ref={(el) => (itemRefs.current[index] = el)}
                                                    onClick={() => selectBikeHandler(bike)}
                                                    style={{
                                                        padding: '10px 12px',
                                                        cursor: 'pointer',
                                                        background: isHighlighted ? '#0284c7' : (selectedBike?.inventory_stock_id === bike.inventory_stock_id ? '#bae6fd' : '#fff'),
                                                        color: isHighlighted ? '#ffffff' : '#0f172a',
                                                        borderBottom: '1px solid #f1f5f9',
                                                        fontSize: '13px'
                                                    }}
                                                >
                                                    <div><b>{bike.model_name}</b></div>
                                                    <div style={{ fontSize: '11px', color: isHighlighted ? '#e0f2fe' : '#64748b' }}>
                                                        Engine: {bike.engine_number} | Chassis: {bike.chassis_number}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            <form onSubmit={handleSingleReceiveSubmit}>
                                <div style={formGroup}>
                                    <label>Document Type:</label>
                                    <select value={singleForm.document_type} onChange={(e) => setSingleForm({...singleForm, document_type: e.target.value})} style={inputStyle}>
                                        <option value="File">File</option><option value="Voucher">Voucher</option>
                                    </select>
                                </div>
                                <div style={formGroup}><label>Physical Location:</label><input type="text" value={singleForm.physical_location} onChange={(e) => setSingleForm({...singleForm, physical_location: e.target.value})} style={inputStyle} /></div>
                                <div style={formGroup}><label>Received By:</label><input type="text" required value={singleForm.received_by} onChange={(e) => setSingleForm({...singleForm, received_by: e.target.value})} style={inputStyle} /></div>
                                <div style={formGroup}><label>Remarks:</label><textarea value={singleForm.remarks} onChange={(e) => setSingleForm({...singleForm, remarks: e.target.value})} style={inputStyle}></textarea></div>
                                <button type="submit" style={{ ...btnPrimary, width: '100%' }}>Receive Document</button>
                            </form>
                        </div>

                        <div style={boxStyle}>
                            <h4>B. Bulk DO / Lot Receiving</h4>
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                <input type="text" placeholder="Enter Lot / DO No" value={lotInput} onChange={(e) => setLotInput(e.target.value)} style={inputStyle} />
                                <button onClick={handleLoadLot} style={btnSecondary}>Load Bikes</button>
                            </div>
                            {lotBikes.length > 0 && (
                                <div style={{ maxHeight: '130px', overflowY: 'auto', border: '1px solid #cbd5e1', padding: '8px', marginBottom: '10px' }}>
                                    <div><input type="checkbox" checked={selectAll} onChange={(e) => { setSelectAll(e.target.checked); setLotBikes(lotBikes.map(b => ({ ...b, checked: e.target.checked }))); }} /> <b>Select All ({lotBikes.length})</b></div>
                                    <hr style={{margin: '5px 0'}}/>
                                    {lotBikes.map(b => (
                                        <div key={b.inventory_stock_id}>
                                            <input type="checkbox" checked={b.checked} onChange={() => setLotBikes(lotBikes.map(x => x.inventory_stock_id === b.inventory_stock_id ? { ...x, checked: !x.checked } : x))} /> Engine: {b.engine_number} | Chassis: {b.chassis_number}
                                        </div>
                                    ))}
                                </div>
                            )}
                            <form onSubmit={handleBulkReceiveSubmit}>
                                <div style={formGroup}>
                                    <label>Document Type:</label>
                                    <select value={bulkForm.document_type} onChange={(e) => setBulkForm({...bulkForm, document_type: e.target.value})} style={inputStyle}>
                                        <option value="File">File</option><option value="Voucher">Voucher</option>
                                    </select>
                                </div>
                                <div style={formGroup}><label>Received By:</label><input type="text" required value={bulkForm.received_by} onChange={(e) => setBulkForm({...bulkForm, received_by: e.target.value})} style={inputStyle} /></div>
                                <div style={formGroup}><label>Remarks:</label><input type="text" value={bulkForm.remarks} onChange={(e) => setBulkForm({...bulkForm, remarks: e.target.value})} style={inputStyle} /></div>
                                <button type="submit" style={{ ...btnSuccess, width: '100%' }}>Bulk Receive Selected</button>
                            </form>
                        </div>
                    </div>

                    <div style={boxStyle}>
                        <h4>C. Document Issuance Section</h4>
                        <form onSubmit={handleIssueSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                            <div style={{ position: 'relative', gridColumn: 'span 2', ...formGroup }}>
                                <label>Search Bike to Issue (Engine / Chassis):</label>
                                <input 
                                    type="text" 
                                    required 
                                    placeholder="Type Engine or Chassis No to search..." 
                                    value={issueQuery} 
                                    onChange={(e) => {
                                        setIssueQuery(e.target.value);
                                        setSelectedIssueBike(null);
                                        setIssueForm({ ...issueForm, inventory_stock_id: '' });
                                    }} 
                                    onKeyDown={handleIssueKeyDown}
                                    style={inputStyle} 
                                />
                                {isIssueDropdownOpen && issueSearchResults.length > 0 && (
                                    <div style={dropdownListStyle}>
                                        {issueSearchResults.map((bike, index) => {
                                            const isHighlighted = issueHighlightedIndex === index;
                                            return (
                                                <div 
                                                    key={bike.inventory_stock_id} 
                                                    ref={(el) => (issueItemRefs.current[index] = el)}
                                                    onClick={() => selectIssueBikeHandler(bike)}
                                                    style={{
                                                        padding: '10px 12px',
                                                        cursor: 'pointer',
                                                        background: isHighlighted ? '#0284c7' : (selectedIssueBike?.inventory_stock_id === bike.inventory_stock_id ? '#bae6fd' : '#fff'),
                                                        color: isHighlighted ? '#ffffff' : '#0f172a',
                                                        borderBottom: '1px solid #f1f5f9',
                                                        fontSize: '13px'
                                                    }}
                                                >
                                                    <div><b>{bike.model_name}</b> (Status: {bike.doc_status})</div>
                                                    <div style={{ fontSize: '11px', color: isHighlighted ? '#e0f2fe' : '#64748b' }}>
                                                        Engine: {bike.engine_number} | Chassis: {bike.chassis_number}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            
                            <div style={formGroup}>
                                <label>Issued Type:</label>
                                <select value={issueForm.issued_type} onChange={(e) => setIssueForm({...issueForm, issued_type: e.target.value})} style={inputStyle}>
                                    <option value="Physical Handover">Physical Handover</option>
                                    <option value="Dispatch">Dispatch</option>
                                </select>
                            </div>

                            {issueForm.issued_type === 'Dispatch' && (
                                <div style={formGroup}>
                                    <label>Dispatch ID *:</label>
                                    <input type="text" required placeholder="Enter Dispatch ID" value={issueForm.dispatch_id} onChange={(e) => setIssueForm({...issueForm, dispatch_id: e.target.value})} style={inputStyle} />
                                </div>
                            )}

                            <div style={formGroup}><label>Issued To:</label><input type="text" required value={issueForm.issued_to} onChange={(e) => setIssueForm({...issueForm, issued_to: e.target.value})} style={inputStyle} /></div>
                            
                            <div style={{ gridColumn: 'span 2', ...formGroup }}><label>Remarks:</label><input type="text" value={issueForm.remarks} onChange={(e) => setIssueForm({...issueForm, remarks: e.target.value})} style={inputStyle} /></div>
                            
                            <div style={{ gridColumn: 'span 2' }}><button type="submit" style={{ ...btnWarning, width: '100%' }}>Issue Document</button></div>
                        </form>
                    </div>
                </div>
            ) : (
                <DocumentReportsPage />
            )}
        </div>
    );
};

const containerStyle = { padding: '20px', background: '#f8fafc', borderRadius: '12px', fontFamily: 'Arial, sans-serif' };
const statsGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginBottom: '20px' };
const cardStyle = { padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' };
const gridTwoCol = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' };
const boxStyle = { background: '#ffffff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 6px rgba(0,0,0,0.05)', marginBottom: '20px' };
const formGroup = { marginBottom: '10px' };
const inputStyle = { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', outline: 'none' };
const dropdownListStyle = { position: 'absolute', top: '100%', left: 0, right: 0, maxHeight: '200px', overflowY: 'auto', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '6px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', zIndex: 50, marginTop: '4px' };
const btnPrimary = { background: '#0284c7', color: '#fff', border: 'none', padding: '10px 15px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' };
const btnSecondary = { background: '#475569', color: '#fff', border: 'none', padding: '8px 15px', borderRadius: '4px', cursor: 'pointer' };
const btnSuccess = { background: '#16a34a', color: '#fff', border: 'none', padding: '8px 15px', borderRadius: '4px', cursor: 'pointer' };
const btnWarning = { background: '#d97706', color: '#fff', border: 'none', padding: '8px 15px', borderRadius: '4px', cursor: 'pointer' };
const navTabBtn = (isActive) => ({ padding: '10px 20px', marginLeft: '10px', background: isActive ? '#0f172a' : '#e2e8f0', color: isActive ? '#fff' : '#000', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' });

export default DocPanel;